import React, { useEffect, useRef } from "react";
import { Gauge, Target, AlertTriangle, TrendingUp, Timer, CalendarDays, ShieldCheck, Scale } from "lucide-react";
import { useApp } from "../context/AppContext";
import { KpiCard } from "../components/KpiCard";
import { GanttTimeline } from "../components/GanttTimeline";
import { GanttLegend } from "../components/GanttLegend";
import { DayNavigator } from "../components/DayNavigator";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { PageHeader } from "../components/PageHeader";

const HARD = [
  "Uniqueness — one workstation per agent per 5-min slot",
  "Continuity — zero gaps (Backoffice fallback)",
  "Meal break — 30 min between 11:00–13:00",
  "Meal service — max 1 agent on break at once",
  "Daily & weekly working-time caps",
  "Cross-day minimum rest between shifts",
  "Skills matching + hardship rotation",
];

export default function Dashboard() {
  const { config, schedule, solve, solving, agents, workstations, absences, professions, selectedDay } = useApp();
  const didAuto = useRef(false);

  useEffect(() => {
    if (config && !schedule && agents.length && !didAuto.current && !solving) {
      didAuto.current = true;
      solve();
    }
  }, [config, schedule, agents, solve, solving]);

  const s = schedule?.stats;

  return (
    <div className="animate-fadeup">
      <PageHeader
        title="Command Center"
        subtitle="Weekly workforce placement solved by Google OR-Tools CP-SAT on a 5-minute grid (rolling per-day horizon, 30 s cap)."
      />

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
        <KpiCard testid="kpi-coverage" label="Weekly coverage" value={s ? s.coverage_pct : "—"} unit="%" tone="success" icon={Gauge} sub={s ? `${s.covered}/${s.total_required} slots` : ""} />
        <KpiCard testid="kpi-penalty" label="Penalty score" value={schedule ? schedule.objective_value?.toLocaleString() : "—"} tone="primary" icon={Target} sub="objective (lower = better)" />
        <KpiCard testid="kpi-critical" label="Critical gaps" value={s ? s.critical_uncovered : "—"} tone={s?.critical_uncovered ? "critical" : "success"} icon={AlertTriangle} sub="priority-1 empty slots" />
        <KpiCard testid="kpi-overtime" label="Overtime" value={s ? s.overtime_hours : "—"} unit="h" tone="warning" icon={TrendingUp} sub="beyond planned shifts" />
        <KpiCard testid="kpi-agents" label="Agent-days" value={s ? s.working_agent_days : "—"} tone="ink" icon={CalendarDays} sub={`${agents.length} agents · 7 days`} />
        <KpiCard testid="kpi-solvetime" label="Solve time" value={schedule ? (schedule.solve_time_ms / 1000).toFixed(1) : "—"} unit="s" tone="ink" icon={Timer} sub={schedule?.status || "idle"} />
      </div>

      {config && (
        <>
          <DayNavigator />
          <GanttTimeline config={config} agents={agents} workstations={workstations} schedule={schedule} absences={absences} professions={professions} day={selectedDay} />
          <GanttLegend workstations={workstations} />
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <Card>
          <CardHeader className="flex-row items-center gap-2 flex">
            <ShieldCheck className="w-4 h-4 text-success" />
            <CardTitle>Hard Constraints — always satisfied</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {HARD.map((h) => (
                <li key={h} className="flex items-start gap-2 text-xs text-muted">
                  <span className="mt-1 w-1.5 h-1.5 rounded-sm bg-success/70 shrink-0" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center gap-2 flex">
            <Scale className="w-4 h-4 text-primary" />
            <CardTitle>Objective — minimised penalties</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {config && Object.entries(config.priority_weights).map(([p, w]) => (
                <Row key={p} label={`Empty slot · Priority ${p}`} value={`${Number(w).toLocaleString()} pts`} />
              ))}
              {config && <Row label="Overtime · per 5-min slot" value={`${config.overtime_weight.toLocaleString()} pts`} highlight />}
              <p className="text-[11px] text-faint pt-2 leading-relaxed">
                A critical gap (100k) dwarfs overtime (5k), so the solver stretches an agent's hours to keep a
                priority-1 desk staffed rather than leaving it empty — always inside the legal daily/weekly & rest limits.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-line last:border-0">
      <span className={`text-xs ${highlight ? "text-warning" : "text-muted"}`}>{label}</span>
      <span className="mono text-xs text-ink">{value}</span>
    </div>
  );
}
